import React from 'react';
import { X, ExternalLink, Facebook, Youtube, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { mockPaintings, paintingImage } from '../data/mockGalleryData';
import { isDonateEnabled } from './DonateModal';
import InquireShare from './InquireShare';
import LikeButton from './LikeButton';
import { paintingSlug } from '../data/mockGalleryData';

const PaintingInfo = ({ 
  isOpen, 
  onClose, 
  currentPaintingIndex,
  onDonate,
  likes,
  onToggleLike
}) => {
  if (!isOpen) return null;

  const painting = mockPaintings[currentPaintingIndex];

  // --- Placeholder Links (REPLACE THESE WITH YOUR ACTUAL URLS) ---
  const facebookPageUrl = "https://www.facebook.com/artbydavidraj/";
  const youtubeChannelUrl = "https://www.youtube.com/watch?v=xLqOO8YPSuc&list=PLRZQDocphGwGWkiXkxtr3-OCvvFgGesd8";
  const instagramProfileUrl = "https://www.instagram.com/davidraj.paul/";
  // --- End Placeholder Links ---

  // Extract video ID from YouTube URL for thumbnail
  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const videoId = getYouTubeVideoId(painting.youtubeUrl);
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden m-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Artwork Details</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </Button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Artwork Image */}
            <div className="space-y-4">
              <div className="aspect-[4/3] overflow-hidden rounded-lg border">
                <img
                  src={paintingImage(painting, 'large')}
                  alt={painting.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* YouTube Video Section */}
              {painting.youtubeUrl && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Artist Process Video
                    </h3>
                    <Button
                      onClick={() => window.open(painting.youtubeUrl, '_blank')}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      size="sm"
                    >
                      <ExternalLink size={16} className="mr-2" />
                      Watch on YouTube
                    </Button>
                  </div>
                  
                  {thumbnailUrl ? (
                    <div 
                      className="aspect-video overflow-hidden rounded-lg border cursor-pointer hover:opacity-80 transition-opacity relative"
                      onClick={() => window.open(painting.youtubeUrl, '_blank')}
                    >
                      <img
                        src={thumbnailUrl}
                        alt={`${painting.title} process video`}
                        className="w-full h-full object-cover"
                      />
                      {/* Play button overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-red-600 hover:bg-red-700 rounded-full p-4 transition-colors">
                          <div className="w-0 h-0 border-l-[12px] border-l-white border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent ml-1"></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-600 text-sm">
                      Click the painting in the gallery twice to watch the process video, or use the YouTube link above.
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Artwork Information */}
            <div className="space-y-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {painting.title}
                </h1>
                <p className="text-xl text-amber-600 font-medium mb-4">
                  by {painting.artist}
                </p>
              </div>
              
              <div className="prose prose-gray">
                <p className="text-gray-700 leading-relaxed">
                  {painting.description}
                </p>
              </div>

              {likes && <LikeButton paintingId={paintingSlug(painting)} likes={likes} onToggle={onToggleLike} size="lg" />}

              <InquireShare painting={painting} />

              {/* --- Social Links Section --- */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-amber-800 mb-2">
                    For more paintings like this, please support me by following on the platforms below:
                </h4>
                <div className="flex items-center justify-center space-x-5">
                  {/* Facebook Link */}
                  {facebookPageUrl !== "https://www.facebook.com/your-artist-page" && (
                    <a href={facebookPageUrl} target="_blank" rel="noopener noreferrer" title="Facebook Page"
                       className="text-gray-500 hover:text-blue-600 transition-colors transform hover:scale-110">
                      <Facebook size={28} />
                    </a>
                  )}
                  {/* YouTube Link */}
                  {youtubeChannelUrl !== "https://www.youtube.com/your-channel" && (
                    <a href={youtubeChannelUrl} target="_blank" rel="noopener noreferrer" title="YouTube Channel"
                       className="text-gray-500 hover:text-red-600 transition-colors transform hover:scale-110">
                      <Youtube size={28} />
                    </a>
                  )}
                  {/* Instagram Link */}
                  {instagramProfileUrl !== "https://www.instagram.com/your-profile" && (
                    <a href={instagramProfileUrl} target="_blank" rel="noopener noreferrer" title="Instagram Profile"
                       className="text-gray-500 hover:text-pink-600 transition-colors transform hover:scale-110">
                      <Instagram size={28} />
                    </a>
                  )}
                </div>
              </div>
              {/* --- End Social Links --- */}

              {isDonateEnabled && onDonate && (
                <Button onClick={onDonate} className="w-full bg-red-500 hover:bg-red-600 text-white">
                  ❤️ Support the Artist
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaintingInfo;